import React, {useCallback, useContext, useEffect, useMemo, useState} from 'react';

import {yupResolver} from '@hookform/resolvers/yup';
import {
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {FormProvider, useForm} from 'react-hook-form';
import {useTranslation} from 'react-i18next';
import {useDispatch, useSelector} from 'react-redux';
import {useLocation} from 'react-router-dom';

import ApptsContext from './ApptsContext.js';
import ApptsDesktop from './ApptsDesktop.js';
import ApptsMobile from './ApptsMobile.js';

import {validateHostConnections} from 'Api/ZCalendar.js';
import {toDateTime} from 'Components/BookingBlocksWidget/utils.js';
import BookingFormContext from 'Components/BookingForm/BookingFormContext.js';
import {getBookerValidationSchema, getBookingFormDefaultValues} from 'Components/BookingForm/yupSchema.js';
import ApiContext from 'Components/Reschedulings/ApiContext.js';
import {ErrorMsgBody500BadCalendar} from 'Error/ErrorMsgConst.js';
import HostContext from 'HostContext.js';
import {
  fetchAvailability,
  fetchExternalUserInfo,
  fetchInitialApptData,
  resetApptStore,
  setApptError,
  setCurrentTimeCutoff,
  setSelectedDate,
  setViewingMonth,
} from 'Store/BookAppointmentsStore.js';
import {setTimeFormat} from 'Store/HostSettingsStore.js';
import {
  defaultTz,
  expandRangeToWeeks,
  getFirstDayOnCalendar,
  getLastDayOnCalendar,
  getTimeZoneList,
  isCciCalendar,
  useQueryWithRetryCaptcha,
} from 'Utils';
import {
  getApptEndDate,
  getWeekAvailabilityKey,
  weekKeyToDt,
} from 'Utils/apptAvailabilityUtils.js';
import {
  AVAIL_STATUS,
} from 'Utils/consts.js';
import DateWithTimeZone from 'Utils/DateWithTimeZone';
import {getDayBoundries, useCushionCutoff} from 'Utils/useDateHooks.js';
import useFontStyle from 'Utils/useFontStyle.js';

import 'react-datepicker/dist/react-datepicker.css';


export const VIEW_STATE = {
  DATE_VIEW: 'date',
  SLOT_VIEW: 'slots',
  FORM_VIEW: 'form',
  CONFIRM_VIEW: 'confirm',
  PAYMENT_VIEW: 'payment',
  PAYMENT_BUT_BOOKING_ERROR_VIEW: 'paymentButBookingError',
  PAYMENT_BOOKING_PAGE_EXPIRED: 'paymentBookingPageExpired',
};

/**
 * Appointments Page Component
 * @param {Object} props
 * @param {String} props.calendarId
 * @param {String} props.appointmentId
 * @return {ReactElement}
 */
export default function Appointments(props) {
  const {appointmentId, calendarId} = props;
  const {t} = useTranslation();
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const paramsValid = !!appointmentId;

  useFontStyle(calendarId);

  const {search} = useLocation();
  const {encodedQuerySlot, queryMonth, querySlot, queryTz} = useMemo(() => {
    const query = new URLSearchParams(search);
    const searchParams = {};
    for (const [key, value] of query.entries()) {
      searchParams[`${key}`] = value;
    }
    let queryTz = query.get('timeZone');
    const timezones = getTimeZoneList();
    if (!timezones.includes(queryTz)) {
      queryTz = null;
    }
    return {
      encodedQuerySlot: query.get('slt') || '',
      querySlot: query.get('slot'),
      queryTz,
      queryMonth: query.get('month'),
    };
  }, [search]);

  const dispatch = useDispatch();

  const location = useLocation();

  const {isHost} = useContext(HostContext);
  const appointment = useSelector((s) => s.bookAppointments.appt);
  const apptDays = useSelector((s) => s.bookAppointments.apptDays);
  const isCciAccount = isCciCalendar(appointment?.organizer?.email);

  const searchParams = useMemo(() => {
    const params = {};
    const query = new URLSearchParams(search);
    for (const [key, value] of query.entries()) {
      params[`${key}`] = value;
    }
    return params;
  }, [search]);
  const [voterInfo] = useState(searchParams.voterId ? searchParams : null);

  const getDefaultValues= useMemo(() => {
    return getBookingFormDefaultValues(appointment, location?.state?.fv, searchParams);
  }, [appointment, location?.state?.fv, searchParams]);
  const bookingContext = useForm({
    defaultValues: getDefaultValues,
    resolver: yupResolver(
      getBookerValidationSchema(
        appointment,
        isCciAccount,
        voterInfo,
        t,
      )
    ),
  });
  const guests = bookingContext.watch('guests');
  const [showGuestEmails, setShowGuestEmails] = useState(false);

  useEffect(() => {
    // if guests are pre-filled, we should show add additional guests UI automatically.
    if (guests.length > 0) {
      setShowGuestEmails(true);
    }
  }, [guests]);

  useEffect(() => {
    return () => {
      dispatch(resetApptStore());
    };
  }, [dispatch]);

  useEffect(() => {
    bookingContext.reset(getDefaultValues);
  }, [bookingContext, getDefaultValues]);

  const [confirmationState, setConfirmationState] = useState({
    event: null,
    userEmail: null,
  });

  const [displayOtp, setDisplayOtp] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const apptErrorState = useSelector((state) => state.bookAppointments.errorState);

  const [bookingFormErrorState, setBookingFormErrorState] = useState({
    isError: false,
    reason: '',
  });

  const [selectedSlot, setSelectedSlot] = useState();

  const api = useContext(ApiContext);
  const doFetchAppt = useQueryWithRetryCaptcha(api.loadAppt, calendarId);
  const doFetchAvail = useQueryWithRetryCaptcha(api.loadApptAvail, calendarId);

  const [currView, setCurrView] = useState(VIEW_STATE.DATE_VIEW);
  const [originalTimeZone, setTimeZone] = useState(queryTz || defaultTz);

  const [bizOrderId, setBizOrderId] = useState('');

  // locked time zone has highest priority
  const timeZone = useMemo(() => {
    return (appointment?.lockTimezoneEnabled)? appointment.lockTimezoneContent: originalTimeZone;
  }, [appointment, originalTimeZone]);

  const troubleShootState = useSelector((s) => s.bookAppointments.troubleShootState);
  /**
   * @param {Slot} slot
   */
  const handleSelectSlot = useCallback((slot) => {
    setSelectedSlot(slot);
    setCurrView(VIEW_STATE.FORM_VIEW);
  }, []);

  const isBookerVoting = useSelector((s) => s.bookAppointments.appt?.bookingMethod) === 'poll';
  const [votes, setVotes] = useState({});

  const handleVotingSlot = (slot) => {
    const curr = {...votes};
    if (curr[slot.start]) {
      delete curr[slot.start];
    } else {
      curr[slot.start] = slot;
    }
    setVotes(curr);
  };

  // TODO back button forcing to go bookingFormScreen
  // could navigate to original link
  const handleBack = () => {
    setBookingFormErrorState({
      isError: false,
      reason: '',
    });
    switch (currView) {
      case VIEW_STATE.SLOT_VIEW:
        setCurrView(VIEW_STATE.DATE_VIEW);
        break;
      case VIEW_STATE.FORM_VIEW:
        setCurrView(VIEW_STATE.SLOT_VIEW);
        setDisplayOtp(false);
        break;
      case VIEW_STATE.CONFIRM_VIEW:
        setCurrView(VIEW_STATE.DATE_VIEW);
        setConfirmationState({
          event: null,
          userEmail: null,
        });
        bookingContext.reset(getDefaultValues);
        break;
      default:
        break;
    }
  };

  const defaultSpots = useSelector((s) => s.bookAppointments.appt?.spots);

  useEffect(() => {
    if (defaultSpots && voterInfo) {
      const curr = {};
      defaultSpots.forEach((spot) => {
        if ((spot?.voters || []).includes(voterInfo.email)) {
          const date = new DateWithTimeZone(spot.start, timeZone);
          curr[date] = {
            start: date,
            voters: spot.voters,
          };
        }
      });
      setVotes(curr);
    }
  }, [defaultSpots, voterInfo, timeZone]);

  const currentTimeCutoff = useCushionCutoff(appointment, getDayBoundries(timeZone));
  const currentTimeCutoffISO = currentTimeCutoff.toISOString();

  useEffect(() => {
    dispatch(setCurrentTimeCutoff(currentTimeCutoffISO));
  }, [dispatch, currentTimeCutoffISO]);

  const fetchAvailableDays = useCallback(async (date, timeZone) => {
    const weekKeys = expandRangeToWeeks(
      getFirstDayOnCalendar(date, timeZone),
      getLastDayOnCalendar(date, timeZone),
      timeZone
    )
      .filter((weekStart) => weekStart.diff(getApptEndDate(appointment, timeZone)).milliseconds < 0)
      .map((dt) => getWeekAvailabilityKey(dt, timeZone));

    if (weekKeys.length && weekKeys.some((key) =>
      !apptDays?.[key]?.error &&
      !apptDays?.[key]?.loading &&
      !apptDays?.[key]?.days
    )) {
      try {
        await dispatch(fetchAvailability({
          fetchApptAvailQuery: {
            query: doFetchAvail,
            args: [
              calendarId,
              appointment.id,
              {
                timeMin: weekKeyToDt(weekKeys[0]).toUTC().toISO(),
                timeMax: weekKeyToDt(weekKeys[weekKeys.length-1]).plus({week: 1, millisecond: -1}).toUTC().toISO(),
                timeZone: timeZone,
              },
            ],
          },
        })).unwrap();
      } catch (e) {
        if (isHost) {
          if (e.message === ErrorMsgBody500BadCalendar && troubleShootState?.attendees) {
            try {
              const hostEmails = troubleShootState?.attendees?.map((attendee) => attendee.email);
              const response = await validateHostConnections(hostEmails);
              const errorUserEmails = response.filter((user) => user?.error).map((obj) => obj.user);
              const display = troubleShootState?.attendees?.filter((attendee) => {
                return (errorUserEmails.includes(attendee.email) && attendee.displayName);
              })
                .map((obj) => {
                  return `${obj.displayName} (${obj.loginEmail})`;
                });
              if (display.length > 0) {
                dispatch(setApptError({
                  isError: true,
                  reason: display.length > 1 ? t('validation.fixCalendars', [display.join(', ')]) :
                t('validation.fixCalendar', [display]),
                }));
              }
            } catch (e) {};
          }
        } else {
          // fetchAppts thunk will handle collecting the final error message reason,
        // just pass it through from this side.
          dispatch(setApptError({
            isError: true,
            reason: e.message,
          }));
        }
      };
    }
  }, [appointment, apptDays, dispatch, doFetchAvail, calendarId, isHost, troubleShootState?.attendees, t]);

  const viewingMonth = useSelector((state) => state.bookAppointments.viewingMonth);
  const onMonthChange = (date) => {
    dispatch(setViewingMonth(date.toISOString()));
  };
  useEffect(() => {
    if (appointment && [VIEW_STATE.DATE_VIEW, VIEW_STATE.SLOT_VIEW].includes(currView)) {
      fetchAvailableDays(toDateTime(viewingMonth, timeZone, true), timeZone);
    }
  }, [appointment, fetchAvailableDays, timeZone, viewingMonth, currView]);

  useEffect(() => {
    const fetchAppt = async () => {
      try {
        const selectedSpotData = await dispatch(fetchInitialApptData({
          fetchApptQuery: {
            query: doFetchAppt,
            args: [
              calendarId,
              appointmentId,
              {
                timeZone: queryTz || defaultTz,
              },
            ],
          },
          fetchApptAvailQuery: {
            query: doFetchAvail,
            args: [
              calendarId,
              appointmentId,
              {
                timeZone: queryTz || defaultTz,
              },
            ],
          },
          queryParams: new URLSearchParams({timeZone: queryTz, slot: querySlot, encodedQuerySlot, month: queryMonth}),
        })).unwrap();
        if (selectedSpotData) {
          // if encodedQuerySlot is set, all available times should not be displayed
          // even though the current time slot is not available
          if (encodedQuerySlot || selectedSpotData.status === AVAIL_STATUS.AVAILABLE) {
            handleSelectSlot({
              start: new Date(selectedSpotData.startTime),
              groupNum: 1,
              availableNumber: 1,
              status: AVAIL_STATUS.AVAILABLE,
            });
          }
        }
      } catch (e) {
        // fetchAppts thunk will handle collecting the final error message reason,
        // just pass it through from this side.
        dispatch(setApptError({
          isError: true,
          reason: e.message,
        }));
      };
    };

    if (calendarId && appointmentId) {
      fetchAppt();
      // Even if it's a team appointment, we fetch showPoweredByZoom value from server
      // to determine if we need to show the powered by zoom logo
      dispatch(fetchExternalUserInfo(calendarId));
    } else {
      dispatch(setApptError({
        isError: true,
        reason: 'error.errorInvalidIdsFromAppt',
      }));
    }
  }, [dispatch, doFetchAppt, doFetchAvail, calendarId, appointmentId,
    encodedQuerySlot, queryTz, querySlot, queryMonth, handleSelectSlot]);

  const [troubleShootingMode, setTroubleShootingMode] = useState(false);

  useEffect(() => {
    if (isHost) {
      setTroubleShootingMode(true);
    }
  }, [isHost]);


  const errorInfo = {
    isError: (!paramsValid) || apptErrorState.isError,
    reason: (!paramsValid) ? t('error.errorInvalidIds') : t(apptErrorState.reason),
  };

  const handleSelectDate = (date) => {
    dispatch(setSelectedDate(date.toISOString()));
    setCurrView(VIEW_STATE.SLOT_VIEW);
  };

  const timeFormat = useSelector((state) => state.hostSettingsState?.timeFormat?.value);
  const handleChangeTimeFormat = (value) => {
    dispatch(setTimeFormat(value));
  };

  return (
    <ApptsContext.Provider value={{
      currView,
      setCurrView,
      handleBack,
      handleChangeTimeFormat,
      timeFormat,
      timeZone,
      setTimeZone,
      votes,
      setVotes,
      isBookerVoting,
      voterInfo,
    }}>
      <FormProvider {...bookingContext}>
        <BookingFormContext.Provider value={{
          currView,
          setCurrView,
          displayOtp,
          setDisplayOtp,
          errorState: bookingFormErrorState,
          setErrorState: setBookingFormErrorState,
          confirmationState,
          setConfirmationState,
          isSubmitting,
          setIsSubmitting,
          showGuestEmails,
          setShowGuestEmails,
          handleBack,
          voterInfo,
          bizOrderId,
          setBizOrderId,
        }}>
          {isSmallScreen ?
        <ApptsMobile
          periodType={appointment?.periodType}
          calendarId={calendarId}
          errorInfo={errorInfo}
          handleSelectDate={handleSelectDate}
          handleSelectSlot={isBookerVoting ? handleVotingSlot : handleSelectSlot}
          selectedSlot={selectedSlot}
          isBookerVoting={isBookerVoting}
          votes={votes}
          setCurrView={setCurrView}
          onMonthChange={onMonthChange}
          voterInfo={voterInfo}
          troubleShootingMode={troubleShootingMode}
          setTroubleShootingMode={setTroubleShootingMode}
        /> :
        <ApptsDesktop
          periodType={appointment?.periodType}
          calendarId={calendarId}
          errorInfo={errorInfo}
          handleSelectDate={handleSelectDate}
          handleSelectSlot={isBookerVoting ? handleVotingSlot : handleSelectSlot}
          selectedSlot={selectedSlot}
          isBookerVoting={isBookerVoting}
          votes={votes}
          setCurrView={setCurrView}
          onMonthChange={onMonthChange}
          voterInfo={voterInfo}
          troubleShootingMode={troubleShootingMode}
          setTroubleShootingMode={setTroubleShootingMode}
        />}
        </BookingFormContext.Provider>
      </FormProvider>
    </ApptsContext.Provider>
  );
}
